// Focus navigator for the four in-run choice panels (pickup card, skill
// shop, growth-spurt chooser, gift chooser) — Phase E of the amoeba-input
// plan. Real DOM focus + programmatic .click(): synthetic KeyboardEvents are
// untrusted and don't fire the panels' own listeners. Ticked BEFORE the
// freeze gates in main.ts — it only matters while a gate is early-returning.
//
// Ownership (no double handling of a justPressed edge): this module owns
// direction nav + menuConfirm for every registered panel, and menuCancel
// ONLY for a panel that passes onCancel (today just the pickup card — shop's
// cancel lives in ui-hotkeys.ts; spurt/gift register with no onCancel
// because the choice is mandatory).

import type { GameActions } from '../game/controls'
import type { Vec2 } from '../engine/input'

export interface FocusPanel {
  isOpen(): boolean
  /** Stable container element; focusables are re-queried from it on every
   *  move because panels re-render their contents in place. */
  root(): HTMLElement
  /** Present only where cancel is a real choice (pickup card: pocket it). */
  onCancel?: () => void
}

/** Repeat rate while a direction is held past the initial edge-fire. */
export const REPEAT_MS = 150
/** Vector component magnitude past this counts as "a direction is held". */
const THRESHOLD = 0.5

/** Wrapping index step — the pure grid-nav math, unit-tested directly. */
export function stepIndex(current: number, delta: number, count: number): number {
  if (count <= 0) return 0
  return ((current + delta) % count + count) % count
}

/** Dominant-axis vector → nav delta. World +y is up (deadzone.ts), and the
 *  spec maps up/left to "previous" (-1), down/right to "next" (+1). */
export function navDelta(v: Vec2): -1 | 0 | 1 {
  const ax = Math.abs(v.x)
  const ay = Math.abs(v.y)
  if (ax < THRESHOLD && ay < THRESHOLD) return 0
  if (ax >= ay) return v.x > 0 ? 1 : -1
  return v.y > 0 ? -1 : 1
}

/** Edge-fire immediately on neutral→direction (or a direction flip);
 *  otherwise throttle repeats to once per REPEAT_MS while a direction holds. */
export function dueToFire(delta: -1 | 0 | 1, lastDelta: -1 | 0 | 1, now: number, lastMoveAt: number): boolean {
  if (delta === 0) return false
  if (delta !== lastDelta) return true
  return now - lastMoveAt >= REPEAT_MS
}

export function createFocusNav(): {
  register(panel: FocusPanel): void
  tick(actions: GameActions): void
} {
  const panels: FocusPanel[] = []
  let openPanel: FocusPanel | null = null
  let lastDelta: -1 | 0 | 1 = 0
  let lastMoveAt = -Infinity

  const focusables = (panel: FocusPanel): HTMLElement[] =>
    Array.from(panel.root().querySelectorAll<HTMLElement>('[data-focus]'))

  // Where focus lands on open: the panel's marked primary choice if any
  // (shop → the Anagenesis row, not the close button), else DOM-first.
  const initialFocus = (els: HTMLElement[]): HTMLElement | undefined =>
    els.find(e => e.hasAttribute('data-focus-initial')) ?? els[0]

  return {
    register(panel) {
      panels.push(panel)
    },
    tick(actions) {
      const panel = panels.find(p => p.isOpen()) ?? null

      if (panel !== openPanel) {
        openPanel = panel
        lastDelta = 0
        lastMoveAt = -Infinity
        if (panel) initialFocus(focusables(panel))?.focus()
      }
      if (!panel) return

      const delta = navDelta(actions.vec('menuNav'))
      const now = performance.now()
      if (dueToFire(delta, lastDelta, now, lastMoveAt)) {
        const els = focusables(panel)
        if (els.length > 0) {
          const current = els.indexOf(document.activeElement as HTMLElement)
          // Focus stolen (e.g. a stray click) → re-anchor instead of stepping
          // from a phantom index.
          const next = current < 0
            ? els.indexOf(initialFocus(els)!)
            : stepIndex(current, delta, els.length)
          els[next].focus()
          els[next].scrollIntoView({ block: 'nearest' })
        }
        lastMoveAt = now
      }
      lastDelta = delta

      if (actions.justPressed('menuConfirm')) {
        const els = focusables(panel)
        const active = document.activeElement as HTMLElement | null
        if (active && els.includes(active)) active.click()
        else els[0]?.focus()
      }
      if (actions.justPressed('menuCancel')) {
        panel.onCancel?.()
      }
    },
  }
}
